import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  clone,
  cloneDeep,
  findIndex,
  get,
  isArray,
  set,
  toString,
} from 'lodash-es';
import {
  AppsFlyerArgs,
  AppsFlyerData,
  AppsFlyerDataKeys,
  BrazeLogCustomEventData,
  BrazeLogPurchaseData,
  CloseWebPopUpData,
  GetLocationData,
  NewOpenAppData,
  OpenAppData,
  OpenWebPopUpData,
  ShareData,
  SyncTokenData,
  WebAppMessage,
  WebAppWindow,
  WriteLogData,
} from '@/types';
import { useSelector, useStore } from 'react-redux';
import {
  ackRefreshToken,
  init,
  receive,
  selectIsTokenRefreshed,
  selectTokenToSend,
  selectWebAppType,
  selectWebAppVersion,
  send,
} from '@/redux/reducers/webapp';
import { Confirm } from 'remodeling-web-mobile-sdk-ui';
import { successLogin } from '@/redux/reducers/auth';
import WebAppService from '@/services/webapp';
import { useRouter } from 'next/router';

declare let window: WebAppWindow;

export type CallbackFunction = (
  message: WebAppMessage<unknown>,
) => Promise<void>;

export interface Callback {
  id: string;
  action: string;
  callback: CallbackFunction;
}

export type GetLocationCallback = (
  message: WebAppMessage<GetLocationData>,
) => Promise<void>;

export type FireNativeBackCallback = (
  message: WebAppMessage<null>,
) => Promise<void>;

const WebAppContext = createContext({
  isAOS: () => false,
  isIOS: () => false,
  isApp: () => false,
  isWeb: () => false,
  canSend: false,
  handleWriteLog: (_) => {},
  handleShare: (_) => {},
  handleOpenWebPopUp: (_) => {},
  handleCloseWebPopUp: (_) => {},
  handleOpenApp: (_) => {},
  handleNewOpenApp: (_) => {},
  handleGetLocation: (_) => {},
  handleFireTouchStart: () => {},
  handleFireTouchEnd: () => {},
  handleAppsFlyer: () => {},
  handleFireNativeBack: () => {},
  handleBrazeLogCustomEvent: () => {},
  handleBrazeLogPurchase: () => {},
  detachCallback: (_) => {},
  webAppVersion: 0,
  webviewType: '',
} as {
  isAOS: () => boolean;
  isIOS: () => boolean;
  isApp: () => boolean;
  isWeb: () => boolean;
  canSend: boolean;
  handleWriteLog: (data: WriteLogData) => void;
  handleShare: (data: ShareData) => void;
  handleOpenWebPopUp: (data: OpenWebPopUpData) => void;
  handleCloseWebPopUp: (data: CloseWebPopUpData) => void;
  handleOpenApp: (data: OpenAppData) => void;
  handleNewOpenApp: (data: NewOpenAppData) => void;
  handleGetLocation: (
    callbackId: string | null,
    callback: GetLocationCallback,
  ) => void;
  handleFireTouchStart: () => void;
  handleFireTouchEnd: () => void;
  handleAppsFlyer: (
    data: AppsFlyerArgs,
    options?: { isStrict?: boolean },
  ) => void;
  handleFireNativeBack: (
    callbackId: string,
    callback: FireNativeBackCallback,
  ) => void;
  handleBrazeLogCustomEvent: (data: BrazeLogCustomEventData) => void;
  handleBrazeLogPurchase: (data: BrazeLogPurchaseData) => void;
  detachCallback: (id: string) => void;
  webAppVersion: number | null;
  webviewType: string;
});

export const useWebApp = () => {
  const context = useContext(WebAppContext);

  if (context === undefined) {
    throw new Error('useWebApp must be used within a WebAppContext');
  }

  return context;
};

export interface WebAppProviderProps {
  children: ReactNode | ReactNode[];
}

const WebAppProvider = ({ children }: WebAppProviderProps) => {
  let messageSeq = 1;

  const router = useRouter();
  const webAppType = useSelector(selectWebAppType);
  const webAppVersion = useSelector(selectWebAppVersion);
  const isAOS = useCallback(() => webAppType === 'AOS', [webAppType]);
  const isIOS = useCallback(() => webAppType === 'IOS', [webAppType]);
  const isApp = useCallback(() => isAOS() || isIOS(), [isAOS, isIOS]);
  const isWeb = useCallback(() => webAppType === 'WEB', [webAppType]);
  const webviewType = useMemo(
    () => router.query?.webviewType as string,
    [router.query?.webviewType],
  );
  const store = useStore();

  const [callbacks, setCallbacks] = useState<Callback[]>([]);
  const attachCallback = useCallback((callback: Callback) => {
    setCallbacks((stack) => {
      const newStack = [...stack];
      const index = findIndex(stack, { id: callback.id });
      if (index < 0) {
        newStack.push(callback);
      }
      return newStack;
    });
  }, []);

  const detachCallback = useCallback((id: string) => {
    setCallbacks((stack) => {
      const newStack = [...stack];
      const index = findIndex(stack, { id });
      if (index >= 0) {
        newStack.splice(index, 1);
      }
      return newStack;
    });
  }, []);

  // web2app part.
  const [canSend, setCanSend] = useState(false);
  const handleSend = useCallback(
    async function <T>(message: WebAppMessage<T>) {
      message._id = messageSeq++;

      setCanSend(false);

      if (window.HSAndroidWeb2App?.postMessage) {
        window.HSAndroidWeb2App.postMessage(JSON.stringify(message));
        setCanSend(true);
        store.dispatch(send(message));
      } else if (isAOS()) {
        setCanSend(false);
      }

      if (window.webkit?.messageHandlers.callbackHandler.postMessage) {
        window.webkit?.messageHandlers.callbackHandler.postMessage(
          JSON.stringify(message),
        );
        setCanSend(true);
        store.dispatch(send(message));
      } else if (isIOS()) {
        setCanSend(false);
      }
    },
    [isAOS, isIOS, store, messageSeq],
  );

  const handleWriteLog = useCallback(
    (data: WriteLogData) => {
      handleSend({
        action: 'writeLog',
        data,
      });
    },
    [handleSend],
  );

  const handleSyncToken = useCallback(
    (data: SyncTokenData) => {
      handleSend({
        action: 'syncToken',
        data,
      });
    },
    [handleSend],
  );
  const isTokenRefreshed = useSelector(selectIsTokenRefreshed);
  const tokenToSend = useSelector(selectTokenToSend);
  useEffect(() => {
    if (isTokenRefreshed) {
      if (tokenToSend) {
        handleSyncToken(tokenToSend);
      }
      store.dispatch(ackRefreshToken());
    }
  }, [handleSyncToken, isTokenRefreshed, store, tokenToSend]);

  const handleShare = useCallback(
    (data: ShareData) => {
      handleSend({
        action: 'share',
        data,
      });
    },
    [handleSend],
  );

  const handleOpenWebPopUp = useCallback(
    ({
      url,
      isReplaceWindow = false,
      shouldFreshWindow = false,
      data = null,
    }: OpenWebPopUpData) => {
      const destination = url.startsWith('http')
        ? url
        : `${process.env.NEXT_PUBLIC_MWEB_HOST}${
            url.startsWith('/') ? '' : '/'
          }${url}`;

      handleSend({
        action: 'openWebPopUp',
        data: {
          url: destination,
          isReplaceWindow,
          shouldFreshWindow,
          data,
        },
      });
    },
    [handleSend],
  );

  const handleCloseWebPopUp = useCallback(
    ({ isReloadWindow = false }: CloseWebPopUpData) => {
      handleSend({
        action: 'closeWebPopUp',
        data: {
          isReloadWindow,
        },
      });
    },
    [handleSend],
  );

  const [isOpenAppAlert, setIsOpenAppAlert] = useState(false);
  const [openAppData, setOpenAppData] = useState<OpenAppData>();
  // eslint-disable-next-line no-unused-vars
  const [_newOpenAppData, setNewOpenAppData] = useState<NewOpenAppData>();
  const handleOpenAppAlertConfirm = useCallback(() => {
    handleSend({
      action: 'openApp',
      data: openAppData,
    });

    if (typeof window !== 'undefined') {
      (window as Window).open(`${process.env.NEXT_PUBLIC_MWEB_HOST}/app/store`);
    }
  }, [handleSend, openAppData]);
  const handleOpenAppAlertClose = useCallback(() => {
    setIsOpenAppAlert(false);
    setOpenAppData(undefined);
  }, []);
  const handleOpenApp = useCallback(
    (data: OpenAppData) => {
      if (isWeb()) {
        setOpenAppData(data);
        setIsOpenAppAlert(true);
      } else {
        handleSend({
          action: 'openApp',
          data,
        });
      }
    },
    [handleSend, isWeb],
  );

  const handleNewOpenApp = useCallback(
    (data: NewOpenAppData) => {
      if (isWeb()) {
        setNewOpenAppData(data);
        setIsOpenAppAlert(true);
      } else {
        handleSend({
          action: 'openApp',
          data,
        });
      }
    },
    [handleSend, isWeb],
  );

  const handleGetLocation = useCallback(
    (callbackId: string | null, callback: GetLocationCallback) => {
      const message: WebAppMessage<GetLocationData> = {
        _id: messageSeq,
        action: 'getLocation',
      };

      if (isWeb()) {
        if (window.navigator && window.navigator.geolocation) {
          window.navigator.geolocation.getCurrentPosition(
            (result) => {
              const { latitude, longitude } = WebAppService.filterLocation(
                result.coords,
              );
              message.code = 200;
              message.message = 'OK';
              message.data = {
                from: 'navigator',
                latitude,
                longitude,
              };
              callback(message);
            },
            (error) => {
              message.code = 900 + error.code;
              message.message = error.message;
              callback(message);
            },
            {
              timeout: 30 * 1000,
            },
          );
        } else {
          message.code = 400;
          message.message = 'not supported geolocation, navigator';
          callback(message);
        }
      }

      if (isApp() && callbackId) {
        attachCallback({
          id: callbackId,
          action: 'getLocation',
          callback: callback as CallbackFunction,
        });
        handleSend({
          action: 'getLocation',
        });
      }
    },
    [attachCallback, handleSend, isApp, isWeb, messageSeq],
  );

  const handleFireTouchStart = useCallback(() => {
    handleSend({
      action: 'fireTouchStart',
    });
  }, [handleSend]);

  const handleFireTouchEnd = useCallback(() => {
    handleSend({
      action: 'fireTouchEnd',
    });
  }, [handleSend]);

  const handleAppsFlyer = useCallback(
    (data: AppsFlyerArgs, { isStrict = false } = {}) => {
      const payload = clone(data);
      if (isStrict) {
        AppsFlyerDataKeys.forEach((key) => {
          const value = get(payload, key, '');
          set(
            payload,
            key,
            isArray(value)
              ? value.map((v) => toString(v)).join(',')
              : toString(value),
          );
        });
      } else {
        for (const key in payload) {
          const value = get(payload, key, '');
          set(
            payload,
            key,
            isArray(value)
              ? value.map((v) => toString(v)).join(',')
              : toString(value),
          );
        }
      }

      handleSend({
        action: 'appsFlyer',
        data: payload as AppsFlyerData<string>,
      });
    },
    [handleSend],
  );

  const handleBrazeLogCustomEvent = useCallback(
    (data: BrazeLogCustomEventData) => {
      handleSend({
        action: 'brazeLogCustomEvent',
        data,
      });
    },
    [handleSend],
  );

  const handleBrazeLogPurchase = useCallback(
    (data: BrazeLogPurchaseData) => {
      handleSend({
        action: 'brazeLogPurchase',
        data,
      });
    },
    [handleSend],
  );

  // app2web part.
  const onWriteLog = useCallback((data: WriteLogData) => {
    console.log(data);
  }, []);

  const onSyncToken = useCallback(
    (data: SyncTokenData) => {
      store.dispatch(successLogin(data));
      // reseting cookies might be required...
    },
    [store],
  );

  const onGetLocation = useCallback(
    (message: WebAppMessage<GetLocationData>) => {
      const responseMessage = cloneDeep(message);
      if (responseMessage.code === 200 && responseMessage.data) {
        const { latitude, longitude } = WebAppService.filterLocation(
          responseMessage.data,
        );
        responseMessage.data.from = 'other';
        responseMessage.data.latitude = latitude;
        responseMessage.data.longitude = longitude;
      }

      callbacks.forEach((callback) => {
        if (callback.action === 'getLocation') {
          callback.callback(responseMessage);
        }
      });
    },
    [callbacks],
  );

  const onFireNativeBack = useCallback(
    (message: WebAppMessage<null>): boolean => {
      let hasCallback = false;

      // <Dropdown /> component 대응
      if (typeof document !== 'undefined' && document.querySelector) {
        const isDropdownOpened = !!document.querySelector(
          '.base-modal li[data-text]',
        );

        if (isDropdownOpened) {
          return hasCallback;
        }
      }

      for (let index = callbacks.length - 1; index >= 0; index--) {
        const callback = callbacks[index];
        if (callback.action === 'fireNativeBack') {
          callback.callback(message);

          hasCallback = true;
          break;
        }
      }

      return !hasCallback;
    },
    [callbacks],
  );
  const handleFireNativeBack = useCallback(
    (callbackId: string, callback: FireNativeBackCallback) => {
      attachCallback({
        id: callbackId,
        action: 'fireNativeBack',
        callback: callback as CallbackFunction,
      });
    },
    [attachCallback],
  );

  const handleMessage = useCallback(
    (message: WebAppMessage<any>) => {
      store.dispatch(receive(message));

      let returnValue: any;

      switch (message.action) {
        case 'writeLog':
          onWriteLog(message.data);
          break;
        case 'syncToken':
          onSyncToken(message.data);
          break;
        case 'getLocation':
          onGetLocation(message);
          break;
        case 'fireNativeBack':
          returnValue = onFireNativeBack(message);
          break;
        default:
      }

      messageSeq++;

      return returnValue;
    },
    [
      store,
      messageSeq,
      onWriteLog,
      onSyncToken,
      onGetLocation,
      onFireNativeBack,
    ],
  );

  const handleMessageEvent = useCallback(
    (event: MessageEvent) => {
      if (event.data) {
        try {
          const payload = JSON.parse(event.data);

          if ('_id' in payload && 'action' in payload) {
            return handleMessage(payload);
          }
        } catch (e) {}
      }
    },
    [handleMessage],
  );

  const handleBindWebApp = useCallback(() => {
    if (window?.navigator?.userAgent) {
      store.dispatch(
        init(new WebAppService().getWebAppInfo(window.navigator.userAgent)),
      );
    }

    window.HSAndroidApp2Web = {
      postMessage: (message: string) => {
        try {
          const payload = JSON.parse(message);

          if ('_id' in payload && 'action' in payload) {
            return handleMessage(payload);
          }
        } catch (e) {}
      },
    };

    window.addEventListener('message', handleMessageEvent);
  }, [handleMessage, handleMessageEvent, store]);
  const handleReleaseWebApp = useCallback(() => {
    window.removeEventListener('message', handleMessageEvent);
  }, [handleMessageEvent]);

  useEffect(() => {
    handleBindWebApp();
    return () => {
      handleReleaseWebApp();
    };
  }, [handleBindWebApp, handleReleaseWebApp, isApp]);

  return (
    <WebAppContext.Provider
      value={{
        isIOS,
        isAOS,
        isApp,
        isWeb,
        canSend,
        handleWriteLog,
        handleShare,
        handleOpenWebPopUp,
        handleCloseWebPopUp,
        handleOpenApp,
        handleNewOpenApp,
        handleGetLocation,
        handleFireTouchStart,
        handleFireTouchEnd,
        handleAppsFlyer,
        handleFireNativeBack,
        handleBrazeLogCustomEvent,
        handleBrazeLogPurchase,
        detachCallback,
        webAppVersion,
        webviewType,
      }}
    >
      {children}
      {openAppData && (
        <OpenAppAlert
          isOpen={isOpenAppAlert}
          handleConfirm={handleOpenAppAlertConfirm}
          handleClose={handleOpenAppAlertClose}
          data={openAppData}
        />
      )}
    </WebAppContext.Provider>
  );
};

export default WebAppProvider;

const OpenAppAlert = ({
  isOpen,
  handleConfirm,
  handleClose,
  data,
}: {
  isOpen: boolean;
  handleConfirm: () => void;
  handleClose: () => void;
  data: OpenAppData;
}) => {
  const title = useMemo(() => {
    switch (data.appLinkTarget) {
      case 'REMODELING_MANAGER':
        return `리모델링 매니저는\n앱에서 만나실 수 있어요.`;
      default:
        return `앱에서 확인하실 수 있어요.`;
    }
  }, [data.appLinkTarget]);
  const subText = useMemo(() => {
    switch (data.appLinkTarget) {
      case 'REMODELING_MANAGER':
        return `고객님만을 위한 한샘 디자이너의 전문적인\n인테리어 컨설팅을 받아보세요.`;
      default:
        return `설치된 앱을 실행합니다. (앱이 설치되어 있지 않은 경우, 설치 화면으로 이동합니다.)`;
    }
  }, [data.appLinkTarget]);

  return (
    <Confirm
      variant="confirm"
      isOpen={isOpen}
      handleCancel={handleClose}
      handleConfirm={handleConfirm}
      confirmText="앱으로 이동"
      isHighlightConfirm
      textOptions={{
        title,
        subText,
      }}
    />
  );
};
